"use client";

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Stat {
  id: string;
  value: number;
  suffix: string;
  label: string;
  icon: string;
  color: string;
}

export default function ExportStatistics() {
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const stats: Stat[] = [
    {
      id: 'stat_volume',
      value: 500,
      suffix: '+ kg',
      label: 'Gold Exported Annually',
      icon: 'ScaleIcon',
      color: 'from-amber-500 to-amber-600'
    },
    {
      id: 'stat_countries',
      value: 15,
      suffix: '+',
      label: 'Countries Served',
      icon: 'GlobeAltIcon',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'stat_buyers',
      value: 50,
      suffix: '+',
      label: 'Active Buyers',
      icon: 'UsersIcon',
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      id: 'stat_purity',
      value: 99.9,
      suffix: '%',
      label: 'Purity Guarantee',
      icon: 'ShieldCheckIcon',
      color: 'from-purple-500 to-purple-600'
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            stats.forEach((stat) => {
              const element = document.getElementById(`metric-${stat.id}`);
              if (element) {
                animateValue(element, 0, stat.value, 2000, stat.suffix);
              }
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  function animateValue(obj: HTMLElement, start: number, end: number, duration: number, suffix: string) {
    let startTimestamp: number | null = null;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const val = progress * (end - start) + start;
      obj.innerHTML = (end % 1 === 0 ? Math.floor(val) : val.toFixed(1)) + suffix;
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }

  return (
    <section ref={sectionRef} className="py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-amber-600/10 animate-pulse" />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 backdrop-blur-sm border border-amber-500/30 rounded-full px-6 py-2 mb-6">
            <Icon name="ChartBarIcon" size={20} className="text-amber-400" />
            <span className="text-sm font-semibold text-amber-200">Our Impact</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold tracking-tight mb-6 text-white">
            Trusted by Global Buyers
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Our numbers speak for themselves
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div 
              key={stat.id} 
              className="group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-all hover:-translate-y-2 hover:shadow-2xl hover:shadow-amber-500/20"
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                <Icon name={stat.icon as any} size={32} className="text-white" />
              </div>
              <p
                id={`metric-${stat.id}`}
                className="text-5xl font-bold tracking-tighter text-white mb-2"
              >
                0{stat.suffix}
              </p>
              <p className="font-medium text-slate-300 text-sm">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}